import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Package,
  Check,
  Plus,
  Search,
  Sparkles,
  ShoppingBag,
  Loader2
} from 'lucide-react';

const STAPLE_CATEGORIES = {
  cooking_essentials: { label: 'Cooking Essentials', items: ['salt', 'pepper', 'olive oil', 'vegetable oil', 'butter'] },
  common_spices: { label: 'Common Spices', items: ['garlic powder', 'onion powder', 'paprika', 'italian seasoning', 'cumin'] },
  baking: { label: 'Baking', items: ['flour', 'sugar', 'brown sugar', 'baking powder', 'vanilla extract'] },
  condiments: { label: 'Condiments', items: ['soy sauce', 'mustard', 'ketchup', 'mayonnaise', 'honey'] }
};

const PantryStaplesControl = () => {
  const { api, isAuthenticated } = useAuth();
  const [staples, setStaples] = useState([]);
  const [allBasics, setAllBasics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [staplesRes, basicsRes] = await Promise.all([
        api.get('/pantry/staples'),
        api.get('/cart/pantry-basics')
      ]);
      
      setStaples(staplesRes.data.user_staples || []);
      setAllBasics(basicsRes.data.basics || []);
    } catch (err) {
      console.error('Failed to load pantry staples:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStaple = async (item) => {
    try {
      setSaving(true);
      const res = await api.post('/pantry/staples/toggle', null, {
        params: { item }
      });
      
      if (res.data.action === 'added') {
        setStaples(prev => [...prev, item]);
        toast.success(`&quot;{item}" added to your staples`);
      } else {
        setStaples(prev => prev.filter(s => s.toLowerCase() !== item.toLowerCase()));
        toast.success(`&quot;{item}" removed from your staples`);
      }
    } catch (err) {
      toast.error('Failed to update staple');
    } finally {
      setSaving(false);
    }
  };

  const isStaple = (item) => {
    return staples.some(s => s.toLowerCase() === item.toLowerCase());
  };

  const getFilteredItems = () => {
    let items = allBasics;
    
    if (activeCategory !== 'all') {
      items = STAPLE_CATEGORIES[activeCategory]?.items || [];
    }
    
    if (searchQuery) {
      items = items.filter(item => 
        item.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return items;
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Card data-testid="pantry-staples-loading">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="pantry-staples-control">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Pantry Staples</CardTitle>
        </div>
        <CardDescription>
          Items you always have on hand. These will be automatically excluded from your grocery lists.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Currently selected staples */}
        {staples.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Check className="w-4 h-4 text-green-600" />
              <span>Your staples ({staples.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {staples.map(item => (
                <Badge
                  key={item}
                  variant="secondary"
                  className="cursor-pointer hover:bg-destructive/10 transition-colors"
                  onClick={() => toggleStaple(item)}
                  data-testid={`staple-badge-${item.replace(/\s+/g, '-')}`}
                >
                  {item}
                  <span className="ml-1 text-muted-foreground">×</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search items to add..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="staples-search-input"
          />
        </div>

        {/* Category tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            <TabsTrigger value="cooking_essentials" className="text-xs">Cooking</TabsTrigger>
            <TabsTrigger value="common_spices" className="text-xs">Spices</TabsTrigger>
            <TabsTrigger value="baking" className="text-xs">Baking</TabsTrigger>
            <TabsTrigger value="condiments" className="text-xs">Condiments</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Available items grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
          {getFilteredItems().map(item => {
            const selected = isStaple(item);
            return (
              <button
                key={item}
                onClick={() => toggleStaple(item)}
                disabled={saving}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-all
                  ${selected 
                    ? 'bg-primary/10 text-primary border border-primary/20' 
                    : 'bg-muted/50 hover:bg-muted border border-transparent'
                  }
                `}
                data-testid={`staple-item-${item.replace(/\s+/g, '-')}`}
              >
                {selected ? (
                  <Check className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <Plus className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                )}
                <span className="truncate capitalize">{item}</span>
              </button>
            );
          })}
        </div>

        {getFilteredItems().length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No items found matching &quot;{searchQuery}&quot;
          </p>
        )}

        {/* Tip */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm">
          <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-amber-800">
            <span className="font-medium">Tip:</span> Mark items like salt, oil, and common spices that you never need to buy. 
            They&apos;ll be automatically skipped when generating grocery lists.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PantryStaplesControl;
